import { useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Cover from '../cover'
import FirstChat from '../firstChat/main'
import Greeting from '../../Greeting'
import { setShowLuancher, setShowTrigger } from 'app/redux/widget/widget.actions'
import { LAUNCHER_CONTENT_STATE } from 'app/constants/launcherContentStates'
import useMediaQuery from 'app/hooks/useMediaQuery'
import Div100vh from 'react-div-100vh'
import { Close } from 'app/components/icons'
import { LOGO_URL } from 'app/utils/urls'
import { triggerWidgetOpenCloseEvent } from 'app/utils/event'
import { showAlwaysFullScreen } from 'app/utils/screen'
import clsx from 'clsx'
import './launcher.scss'

const getShowLauncher = ({ widgetData }) => widgetData.widgetInfo.showLauncher
const getAnimateWidget = ({ widgetData }) => widgetData.widgetInfo.animateWidget
const getLauncherInfo = ({ widgetData }) => widgetData.widgetInfo.launcherInfo
const getAllowCustomLogo = ({ widgetData }) => widgetData.widgetInfo.data.allowCustomLogo
const getCoverIcon = ({ widgetData }) => widgetData.widgetInfo.data.coverIcon
const getIsWidgetWideOpen = ({ widgetData }) => widgetData.widgetInfo.isWidgetWideOpen
const getChannelId = ({ widgetData }) => widgetData.widgetInfo.data?.channel?.id
const getShowTrigger = ({ widgetData }) => widgetData.widgetInfo.showTrigger
const getInjectedStyle = ({ widgetData }) => widgetData.injectedStyles
const getAllDepartments = ({ messagesData }) => messagesData.departments.data
const getActiveAutoMsg = ({ widgetData }) => widgetData.activeTriggerMsg

const displayModesGreetingDialogueShouldAppear = ['complete', 'short']

const Luancher = () => {
  const smallWidth = useMediaQuery('(max-width: 700px)')
  const IsMobileView = useMediaQuery('(max-width: 450px)')
  const dispatch = useDispatch()

  const showLauncher = useSelector(getShowLauncher)
  const animateWidget = useSelector(getAnimateWidget)
  const launcherInfo = useSelector(getLauncherInfo)
  const allowCustomLogo = useSelector(getAllowCustomLogo)
  const coverIcon = useSelector(getCoverIcon)
  const isWidgetWideOpen = useSelector(getIsWidgetWideOpen)
  const channel_id = useSelector(getChannelId)
  const showTrigger = useSelector(getShowTrigger)
  const injectedStyles = useSelector(getInjectedStyle)
  const allDepartments = useSelector(getAllDepartments)
  const activeAutoMessage = useSelector(getActiveAutoMsg)

  const showGreetingDialogue =
    activeAutoMessage && displayModesGreetingDialogueShouldAppear.includes(activeAutoMessage?.displayMode)

  const getDepartmentsNotSeenReceivedMsgsCount = () =>
    allDepartments.reduce((sum, dp) => (sum += dp.notSeenMsgsCount), 0)

  const allNotSeenReceivedMsgsCount = getDepartmentsNotSeenReceivedMsgsCount()
  const shouldRenderNotifBadge = allNotSeenReceivedMsgsCount > 0

  // show widget fullscreen on widget page
  const showFullscreen = showAlwaysFullScreen(channel_id)

  const customCssVariables = {
    '--raychat-launcher-cover-image': `url(${coverIcon ?? `${LOGO_URL}/1.svg`})`,
    '--raychat-widget-width': isWidgetWideOpen ? '688px' : '366px',
    // injected styles by user through widget APIs.
    '--raychat-widget-custom-bg':
      injectedStyles && injectedStyles.backgroundColor ? injectedStyles.backgroundColor : 'none',
    '--raychat-widget-custom-br':
      injectedStyles && injectedStyles.borderRadius ? injectedStyles.borderRadius : 'inherit',
    '--raychat-widget-custom-width': injectedStyles && injectedStyles.width ? injectedStyles.width : 'auto',
    '--raychat-widget-custom-height': injectedStyles && injectedStyles.height ? injectedStyles.height : 'auto',
    '--raychat-widget-custom-bs': injectedStyles && injectedStyles.boxShadow ? injectedStyles.boxShadow : 'none',
  }

  const handleClick = () => {
    const newStatus = !showLauncher

    dispatch(setShowLuancher(newStatus))

    triggerWidgetOpenCloseEvent(newStatus)
  }

  const renderTriggerNotifBadge = shouldRenderNotifBadge && (
    <div className="sc_new_messsages_count">{allNotSeenReceivedMsgsCount}</div>
  )

  const customClassList = useMemo(
    () => [
      injectedStyles && injectedStyles.backgroundColor ? 'custom_bg' : '',
      injectedStyles && injectedStyles.borderRadius ? 'custom_br' : '',
      injectedStyles && injectedStyles.width ? 'custom_width' : '',
      injectedStyles && injectedStyles.height ? 'custom_height' : '',
      injectedStyles && injectedStyles.boxShadow ? 'custom_bs' : '',
    ],
    [injectedStyles],
  )

  const classList = [showLauncher ? 'launcher_opened' : '', 'launcher_centered ']

  let chatClassList = [
    'sc_chat_window',
    'animate__animated animate__fadeIn',
    showLauncher ? 'launcher_opened' : 'launcher',
    showFullscreen || IsMobileView ? 'fullscreen_chat_window' : '',
  ]

  const launcherClassList = [
    'sc_launcher',
    'launcher_centered ',
    showLauncher ? 'launcher_hidden' : '',
    animateWidget.status && `animate__animated animate__${animateWidget.animationType}`,
    showTrigger && activeAutoMessage ? 'sc_launcher_position' : '', // set trigger position when showing auto message
  ]

  const renderLuancherContent = (
    <>
      {launcherInfo.content === LAUNCHER_CONTENT_STATE.MAIN_COVER && <Cover />}
      {launcherInfo.content === LAUNCHER_CONTENT_STATE.FIRST_CHAT && <FirstChat />}
    </>
  )

  const getLuancher = () => {
    return (
      <>
        {smallWidth ? (
          <Div100vh className="launcher_main_container">
            <div
              style={{ height: '100%' }}
              className={(showLauncher ? 'launcher_opened' : 'launcher', chatClassList.join(' '))}
            >
              {renderLuancherContent}
            </div>
          </Div100vh>
        ) : (
          <div className={chatClassList.join(' ')}>{renderLuancherContent}</div>
        )}
      </>
    )
  }

  useEffect(() => {
    if (showGreetingDialogue) {
      dispatch(setShowTrigger(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGreetingDialogue])

  return (
    <div className="sc_launcher_wrapper" style={customCssVariables}>
      <div className="injectedStyles" />
      <div className={launcherClassList.join(' ')}>
        <div id="animation-root" className={`sc_launcher_inner ${customClassList.join(' ')}`}>
          {renderTriggerNotifBadge}
          <div className={classList.join(' ')} onClick={handleClick}>
            <Close
              className={clsx('sc-open-icon', showLauncher ? 'show_open_icon' : 'hide_open_icon')}
              color="#ffffff"
            />

            {/* launcher cover image */}
            <span
              className={clsx(
                'sc-closed-icon',
                allowCustomLogo && 'sc-closed-icon-custom-logo',
                showLauncher ? 'hide_close_icon' : 'show_close_icon',
              )}
            />
          </div>
        </div>
      </div>

      {showTrigger && <Greeting />}
      {showLauncher && getLuancher()}
    </div>
  )
}

export default Luancher
